/**
 * share.ts — Platform-aware share utility.
 *
 * Uses @capacitor/share on native for the OS share sheet (better UX,
 * more targets like iMessage, WhatsApp, etc.). Falls back to
 * Web Share API, then clipboard copy on platforms without share support.
 *
 * Pattern matches haptics.ts — no-ops silently on failure.
 */

import { Capacitor } from "@capacitor/core"
import { writeClipboard } from "./clipboard"
import { isTauriDesktop } from "./desktopDetection"
import { webOriginForApi } from "./webOrigin"
import { trackAppShared } from "@/shared/services/analyticsTrackers"

const NATIVE_API_BASE = import.meta.env?.VITE_API_BASE_URL?.startsWith("http")
  ? import.meta.env.VITE_API_BASE_URL
  : (import.meta.env?.VITE_API_BASE_URL_NATIVE ?? "https://api.totodo.io")

/** Public web origin used in the native shells, where the page origin is not a web address. */
const APP_ORIGIN = webOriginForApi(NATIVE_API_BASE) ?? "https://totodo.app"

/**
 * Get the correct public origin for the app.
 * In the native shells `window.location.origin` is `capacitor://localhost` (iOS), `https://localhost`
 * (Android) or `tauri://localhost` / `http://tauri.localhost` (desktop), none of them shareable, so
 * the web app of this build's API is used instead.
 */
export function getAppOrigin(): string {
  if (Capacitor.isNativePlatform() || isTauriDesktop()) return APP_ORIGIN
  return window.location.origin
}

/**
 * Build a shareable app URL from a path.
 * Replaces capacitor://localhost with https://totodo.app on native.
 */
export function getAppUrl(path: string): string {
  return `${getAppOrigin()}${path}`
}

export interface ShareOptions {
  /** Title shown in the share sheet header */
  title?: string
  /** Body text content */
  text?: string
  /** URL to share (appended or shown separately depending on OS) */
  url?: string
  /** Optional dialog title (Android only) */
  dialogTitle?: string
}

export interface ShareResult {
  /** Whether the content was shared (true) or cancelled/fell back to copy */
  shared: boolean
  /** If not shared via sheet, whether it was copied to clipboard as fallback */
  copiedToClipboard: boolean
}

/**
 * Share content via the platform share sheet.
 *
 * Priority:
 * 1. Native: @capacitor/share → OS share sheet
 * 2. Web: navigator.share (supported on mobile browsers + some desktop)
 * 3. Fallback: copy text to clipboard
 */
export async function shareContent(
  options: ShareOptions,
): Promise<ShareResult> {
  // 1. Try Capacitor Share on native
  if (Capacitor.isNativePlatform()) {
    try {
      const { Share } = await import("@capacitor/share")
      await Share.share({
        title: options.title,
        text: options.text,
        url: options.url,
        dialogTitle: options.dialogTitle,
      })
      trackAppShared("other")
      return { shared: true, copiedToClipboard: false }
    } catch {
      // User cancelled or plugin unavailable — fall through
    }
  }

  // 2. Try Web Share API
  if (typeof navigator !== "undefined" && "share" in navigator) {
    try {
      await navigator.share({
        title: options.title,
        text: options.text,
        url: options.url,
      })
      trackAppShared("other")
      return { shared: true, copiedToClipboard: false }
    } catch {
      // User cancelled or API error — fall through to clipboard
    }
  }

  // 3. Fallback: copy to clipboard
  const fallbackText = [options.title, options.text, options.url]
    .filter(Boolean)
    .join("\n")

  if (fallbackText) {
    await writeClipboard(fallbackText)
    return { shared: false, copiedToClipboard: true }
  }

  return { shared: false, copiedToClipboard: false }
}

/**
 * Check if the platform supports a native share sheet.
 * Useful for conditionally showing share buttons vs copy buttons.
 */
export function canShare(): boolean {
  if (Capacitor.isNativePlatform()) return true
  return typeof navigator !== "undefined" && "share" in navigator
}
